import { NextPage } from 'next';
import Head from 'next/head';
import { Inter } from 'next/font/google';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import s from '@styles/underConstruction.module.scss';
const inter = Inter({
  subsets: ['latin'],
  weight: ['300', '400', '600']
});
const DEFAULT_MP4 = '/under-construction.mp4';
const DEFAULT_WEBM = '/under-construction.webm';
const customVideoSrc = process.env.NEXT_PUBLIC_UNDER_CONSTRUCTION_VIDEO_SRC;
const customWebmSrc = process.env.NEXT_PUBLIC_UNDER_CONSTRUCTION_WEBM_SRC;
const INSTAGRAM_HREF = 'https://www.instagram.com/rtoys.artists?igsh=MWxrNWgyZDl1bG5qZA==';
const SVG_NS = 'http://www.w3.org/2000/svg';
function paintGlassTitle(block: HTMLElement, ghost: HTMLElement, layer: HTMLElement, svg: SVGSVGElement): boolean {
  const rect = block.getBoundingClientRect();
  const W = rect.width;
  const H = rect.height;
  if (W < 1 || H < 1) return false;
  const DPR = Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2);
  const cs = getComputedStyle(ghost);
  const fs = parseFloat(cs.fontSize);
  const lh = fs * 1.0;
  const kern = -0.03 * fs;
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(W * DPR);
  canvas.height = Math.ceil(H * DPR);
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;
  ctx.scale(DPR, DPR);
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = '#fff';
  const fontFamily = cs.fontFamily;
  ctx.font = `600 ${fs}px ${fontFamily}`;
  ctx.textAlign = 'center';
  const ctxWithSpacing = ctx as CanvasRenderingContext2D & {
    letterSpacing?: string;
  };
  if ('letterSpacing' in ctxWithSpacing) ctxWithSpacing.letterSpacing = `${kern}px`;
  const m = ctx.measureText('Coming');
  const asc = m.actualBoundingBoxAscent ?? fs * 0.76;
  const desc = m.actualBoundingBoxDescent ?? fs * 0.2;
  const totalH = asc + desc + lh;
  const y0 = (H - totalH) / 2 + asc;
  const y1 = y0 + lh;
  ctx.fillText('Coming', W / 2, y0);
  ctx.fillText('soon', W / 2, y1);
  const maskUrl = canvas.toDataURL();
  Object.assign(layer.style, {
    webkitMaskImage: `url("${maskUrl}")`,
    maskImage: `url("${maskUrl}")`,
    webkitMaskSize: `${W}px ${H}px`,
    maskSize: `${W}px ${H}px`,
    webkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    webkitMaskPosition: '0 0',
    maskPosition: '0 0'
  });
  while (svg.firstChild) svg.removeChild(svg.firstChild);
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svg.setAttribute('width', String(W));
  svg.setAttribute('height', String(H));
  const textStyle = `
        font-family: ${fontFamily};
        font-weight: 600;
        font-size: ${fs}px;
        letter-spacing: ${kern}px;
    `;
  const words: {
    word: string;
    y: number;
  }[] = [{
    word: 'Coming',
    y: y0
  }, {
    word: 'soon',
    y: y1
  }];
  words.forEach(({
    word,
    y
  }) => {
    const fill = document.createElementNS(SVG_NS, 'text');
    fill.setAttribute('x', String(W / 2));
    fill.setAttribute('y', String(y));
    fill.setAttribute('text-anchor', 'middle');
    fill.setAttribute('fill', 'rgba(255,255,255,0.06)');
    fill.setAttribute('stroke', 'none');
    fill.style.cssText = textStyle;
    fill.textContent = word;
    svg.appendChild(fill);
    const edge1 = document.createElementNS(SVG_NS, 'text');
    edge1.setAttribute('x', String(W / 2));
    edge1.setAttribute('y', String(y));
    edge1.setAttribute('text-anchor', 'middle');
    edge1.setAttribute('fill', 'none');
    edge1.setAttribute('stroke', 'rgba(255,255,255,0.60)');
    edge1.setAttribute('stroke-width', '0.7');
    edge1.style.cssText = textStyle;
    edge1.textContent = word;
    svg.appendChild(edge1);
    const edge2 = document.createElementNS(SVG_NS, 'text');
    edge2.setAttribute('x', String(W / 2));
    edge2.setAttribute('y', String(y + 1));
    edge2.setAttribute('text-anchor', 'middle');
    edge2.setAttribute('fill', 'none');
    edge2.setAttribute('stroke', 'rgba(255,255,255,0.18)');
    edge2.setAttribute('stroke-width', '0.5');
    edge2.style.cssText = textStyle;
    edge2.textContent = word;
    svg.appendChild(edge2);
  });
  return true;
}
async function waitForTitleLayout() {
  await new Promise<void>(r => requestAnimationFrame(() => r()));
  await new Promise<void>(r => requestAnimationFrame(() => r()));
}

/** Only the headline font — avoids waiting for every font on the site via document.fonts.ready */
async function ensureHeadlineFont() {
  const stack = inter.style.fontFamily;
  try {
    await Promise.race([document.fonts.load(`600 80px ${stack}`), new Promise<void>(r => setTimeout(r, 2500))]);
  } catch {
    /* non-blocking */
  }
}
const UnderConstructionPage: NextPage = () => {
  const [videoOk, setVideoOk] = useState(true);
  const [titleGlassReady, setTitleGlassReady] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLSpanElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const onVideoError = useCallback(() => {
    setVideoOk(false);
  }, []);
  const runPaint = useCallback(async () => {
    const block = blockRef.current;
    const ghost = ghostRef.current;
    const layer = layerRef.current;
    const svg = svgRef.current;
    if (!block || !ghost || !layer || !svg) return;
    await ensureHeadlineFont();
    await waitForTitleLayout();
    let ok = paintGlassTitle(block, ghost, layer, svg);
    if (!ok) {
      await waitForTitleLayout();
      ok = paintGlassTitle(block, ghost, layer, svg);
    }
    if (ok) setTitleGlassReady(true);
  }, []);
  useLayoutEffect(() => {
    void runPaint();
  }, [runPaint]);
  useEffect(() => {
    let t: ReturnType<typeof setTimeout>;
    const schedulePaint = () => {
      clearTimeout(t);
      t = setTimeout(() => void runPaint(), 150);
    };
    window.addEventListener('resize', schedulePaint);
    window.visualViewport?.addEventListener('resize', schedulePaint);
    window.visualViewport?.addEventListener('scroll', schedulePaint);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', schedulePaint);
      window.visualViewport?.removeEventListener('resize', schedulePaint);
      window.visualViewport?.removeEventListener('scroll', schedulePaint);
    };
  }, [runPaint]);
  return <>
            <Head data-sentry-element="Head" data-sentry-source-file="under-construction.tsx">
                <title>r.toys — coming soon</title>
                <meta name='robots' content='noindex, nofollow' data-sentry-element="meta" data-sentry-source-file="under-construction.tsx" />
                <meta name='viewport' content='width=device-width, initial-scale=1.0, viewport-fit=cover' data-sentry-element="meta" data-sentry-source-file="under-construction.tsx" />
            </Head>
            <main className={`${inter.className} ${s.root}`}>
                {videoOk ? <video className={s.bgVideo} autoPlay muted loop playsInline preload='metadata' onError={onVideoError}>
                        {customVideoSrc ? <>
                                {customWebmSrc ? <source src={customWebmSrc} type='video/webm' /> : null}
                                <source src={customVideoSrc} type='video/mp4' />
                            </> : <>
                                <source src={DEFAULT_WEBM} type='video/webm' />
                                <source src={DEFAULT_MP4} type='video/mp4' />
                            </>}
                    </video> : null}
                <div className={s.overlay} />

                <div className={s.container}>
                    <div ref={blockRef} className={s.titleBlock}>
                        <span ref={ghostRef} className={s.titleGhost} aria-hidden='true'>
                            Coming
                            <br />
                            soon
                        </span>
                        <div ref={layerRef} className={`${s.glassLayer} ${titleGlassReady ? s.glassLayerVisible : ''}`} />
                        <svg ref={svgRef} className={s.titleSvg} aria-label='Coming soon' role='img' data-sentry-element="svg" data-sentry-source-file="under-construction.tsx" />
                    </div>

                    <p className={s.subtitle}>
                        We&apos;re working on something special.
                        <br />
                        Follow us to be the first to know.
                    </p>

                    <a className={s.btn} href={INSTAGRAM_HREF} target='_blank' rel='noopener noreferrer'>
                        <svg className={s.btnSvg} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' data-sentry-element="svg" data-sentry-source-file="under-construction.tsx">
                            <rect x='2' y='2' width='20' height='20' rx='5' ry='5' data-sentry-element="rect" data-sentry-source-file="under-construction.tsx" />
                            <circle cx='12' cy='12' r='4' data-sentry-element="circle" data-sentry-source-file="under-construction.tsx" />
                            <circle cx='17.5' cy='6.5' r='0.5' fill='currentColor' stroke='none' data-sentry-element="circle" data-sentry-source-file="under-construction.tsx" />
                        </svg>
                        Follow on Instagram
                    </a>

                    {!videoOk ? <p className={s.videoFallback}>
                            Add <code>public/under-construction.webm</code> and{' '}
                            <code>public/under-construction.mp4</code>, or set{' '}
                            <code>NEXT_PUBLIC_UNDER_CONSTRUCTION_VIDEO_SRC</code> (and optionally{' '}
                            <code>NEXT_PUBLIC_UNDER_CONSTRUCTION_WEBM_SRC</code>).
                        </p> : null}
                </div>
            </main>
        </>;
};
export default UnderConstructionPage;